import BackArrowCircleIcon from '@assets/icons/back-arrow-circle-large.svg';
import i18n from '@/utils/i18n';
import Modal from 'react-native-modal';
import React, { useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DeletePhotoActionSheet } from './delete-photo-action-sheet';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { ImageWithLoading } from '@/components/image-with-loading';
import { Text, TouchableOpacity } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Zoom } from 'react-native-reanimated-zoom';

interface Props {
  currentIndex: number;
  isVisible: boolean;
  onClose: () => void;
  onDeletePhoto: (index: number) => void;
  photos: string[];
}

export const SingleImagePreviewModal: React.FC<Props> = ({
  isVisible,
  onClose,
  onDeletePhoto,
  photos,
  currentIndex,
}) => {
  const [styles, theme] = useStyles();

  const { top, bottom } = useSafeAreaInsets();
  const isZoomedRef = useRef(false);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);

  return (
    <Modal
      animationIn="fadeIn"
      animationOut="fadeOut"
      backdropColor={theme.colors.black}
      backdropOpacity={1}
      hideModalContentWhileAnimating
      isVisible={isVisible}
      onBackButtonPress={onClose}
      onBackdropPress={onClose}
      style={[styles.modal, { marginBottom: bottom, marginTop: top }]}
      testID="image-preview-modal"
      useNativeDriver
    >
      <TouchableOpacity
        onPress={onClose}
        style={styles.closeButton}
        testID="close-preview-button"
      >
        <BackArrowCircleIcon fill={theme.colors.white} />
      </TouchableOpacity>
      <GestureHandlerRootView style={styles.gestureHandler}>
        <Zoom
          maximumZoomScale={4}
          onZoomBegin={() => (isZoomedRef.current = true)}
          onZoomEnd={() => (isZoomedRef.current = false)}
          style={styles.gestureHandler}
        >
          <ImageWithLoading
            contentFit="cover"
            source={{ uri: photos[currentIndex] }}
            style={styles.image}
          />
        </Zoom>
      </GestureHandlerRootView>
      <TouchableOpacity
        hitSlop={20}
        onPress={() => setIsDeleteConfirmVisible(true)}
        style={styles.deleteButton}
        testID="delete-preview-photo-button"
      >
        <Text style={[TYPOGRAPHY.pSemiBold, styles.deleteButtonText]}>
          {i18n.t('delete')}
        </Text>
      </TouchableOpacity>
      <DeletePhotoActionSheet
        isVisible={isDeleteConfirmVisible}
        onClose={() => setIsDeleteConfirmVisible(false)}
        onDelete={() => {
          onDeletePhoto(currentIndex);
          setIsDeleteConfirmVisible(false);
        }}
      />
    </Modal>
  );
};

const useStyles = createStyles((theme) => ({
  closeButton: {
    left: 16,
    position: 'absolute',
    top: 16,
    zIndex: 1,
  },
  deleteButton: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  deleteButtonText: {
    color: theme.colors.white,
    fontSize: 18,
  },
  gestureHandler: {
    flex: 1,
  },
  image: {
    borderRadius: 16,
    flex: 1,
  },
  modal: {
    flex: 1,
    margin: 8,
  },
}));
